import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle2, Clock, MessageCircle, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const PracticeTestResult = () => {
  const { attemptId } = useParams();
  const [attempt, setAttempt] = useState(null);
  const [settings, setSettings] = useState(null);
  const mistakeOptions = [
    'Conceptual',
    'Calculation',
    'Misreading',
    'Casual',
    'Time Management'
  ];

  useEffect(() => {
    fetchAttempt();
    fetchSettings();
  }, [attemptId]);

  const fetchAttempt = async () => {
    try {
      const response = await axios.get(`${API}/practice-history/${attemptId}`);
      const summaries = (response.data.result?.question_summaries || []).map((item, index) => ({
        ...item,
        local_id: item.question_id || `q-${index}`
      }));
      setAttempt({ ...response.data, result: { ...response.data.result, question_summaries: summaries } });
    } catch (error) {
      toast.error('Failed to load practice attempt');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.warn('Settings load failed (non-critical):', error?.message);
    }
  };

  const counselorWhatsapp = useMemo(() => settings?.counselor_whatsapp?.replace(/[^0-9]/g, ''), [settings]);

  const handleMistakeChange = (questionId, value) => {
    setAttempt((prev) => {
      if (!prev?.result?.question_summaries) return prev;
      const updated = prev.result.question_summaries.map((item) =>
        item.local_id === questionId ? { ...item, mistake_type: value } : item
      );
      return { ...prev, result: { ...prev.result, question_summaries: updated } };
    });
  };

  const handleSaveMistakes = async () => {
    if (!attempt?.id) return;
    const summaries = attempt.result?.question_summaries || [];
    const mistakes = summaries
      .filter((item) => !item.is_correct && item.mistake_type)
      .map((item) => ({ question_id: item.question_id || item.local_id, mistake_type: item.mistake_type }));
    try {
      const response = await axios.put(`${API}/practice-history/${attempt.id}/mistakes`, { mistakes });
      setAttempt((prev) => {
        const mergedSummaries = (response.data.question_summaries || prev.result.question_summaries || []).map((item, index) => ({
          ...item,
          local_id: item.question_id || prev.result.question_summaries?.[index]?.local_id || `q-${index}`
        }));
        return { ...prev, result: { ...prev.result, ...response.data, question_summaries: mergedSummaries } };
      });
      toast.success('Mistake patterns updated');
    } catch (error) {
      toast.error('Failed to update mistake patterns');
    }
  };

  if (!attempt) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="practice-result-loading">
        <div className="text-lg text-slate-600">Loading practice result...</div>
      </div>
    );
  }

  const result = attempt.result || {};

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="practice-result-page">
      <div className="mb-6">
        <Link to="/practice/history" className="text-sm text-slate-500 hover:text-slate-700">← Back to Practice History</Link>
        <h1 className="text-4xl font-bold text-slate-900 mt-2">{attempt.exam} Practice Test</h1>
        <p className="text-slate-600">{attempt.mode === 'full' ? 'Full Test' : 'Part Test'} · {attempt.level}</p>
      </div>

      <div className="space-y-6" data-testid="practice-test-results">
        <Card className="border-0 shadow-xl bg-gradient-to-br from-emerald-50 to-teal-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              Your Score
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <p className="text-4xl font-bold text-slate-900" data-testid="result-score">
                {result.score} / {result.max_score}
              </p>
              <p className="text-slate-600" data-testid="result-percentage">{result.percentage}% overall</p>
              <div className="text-sm text-slate-500 mt-1 space-y-0.5" data-testid="result-score-breakdown">
                <p>
                  <span className="text-emerald-600 font-semibold">Positive +{result.positive_score ?? result.score}</span>
                  {' · '}
                  <span className="text-rose-600 font-semibold">Negative −{result.penalty ?? 0}</span>
                </p>
                <p>
                  {result.correct_count ?? 0} of {result.total_questions ?? '—'} correct
                  {result.accuracy !== undefined && <> · {result.accuracy}% accuracy</>}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <Clock className="w-4 h-4" />
              <span>Instant analysis generated</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-violet-600" />
              Performance Analysis
            </CardTitle>
            <CardDescription>{result.analysis?.overall_insights}</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="subjects">
              <TabsList className="mb-4">
                <TabsTrigger value="subjects">Subjects</TabsTrigger>
                <TabsTrigger value="chapters">Chapters</TabsTrigger>
                <TabsTrigger value="topics">Topics</TabsTrigger>
                <TabsTrigger value="mistakes">Mistakes</TabsTrigger>
                <TabsTrigger value="time">Time</TabsTrigger>
              </TabsList>
              <TabsContent value="subjects">
                <div className="space-y-3">
                  {Object.entries(result.analysis?.subject_analysis || {}).map(([key, value]) => (
                    <div key={key} className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                      <h4 className="font-semibold text-slate-900 mb-2">{key}</h4>
                      <p className="text-slate-700">{value}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>
              <TabsContent value="chapters">
                <div className="space-y-3">
                  {Object.entries(result.analysis?.chapter_analysis || {}).map(([key, value]) => (
                    <div key={key} className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                      <h4 className="font-semibold text-slate-900 mb-2">{key}</h4>
                      <p className="text-slate-700">{value}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>
              <TabsContent value="topics">
                <div className="space-y-3">
                  {Object.entries(result.analysis?.topic_analysis || {}).map(([key, value]) => (
                    <div key={key} className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                      <h4 className="font-semibold text-slate-900 mb-2">{key}</h4>
                      <p className="text-slate-700">{value}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>
              <TabsContent value="mistakes">
                <div className="space-y-3">
                  {Object.entries(result.analysis?.mistake_patterns || {}).map(([key, value]) => (
                    <div key={key} className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                      <h4 className="font-semibold text-amber-800 mb-2">{key}</h4>
                      <p className="text-amber-800">{value}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>
              <TabsContent value="time">
                <div className="space-y-3">
                  {Object.entries(result.analysis?.time_efficiency || {}).map(([key, value]) => (
                    <div key={key} className="p-4 rounded-xl bg-violet-50 border border-violet-200">
                      <h4 className="font-semibold text-violet-800 mb-2">{key}</h4>
                      <p className="text-violet-800">{value}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {result.analysis?.recommendations?.length > 0 && (
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                Implementable Suggestions
              </CardTitle>
              <CardDescription>{result.analysis?.way_forward}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {result.analysis.recommendations.map((rec, index) => (
                <motion.div
                  key={rec}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200"
                >
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <p className="text-slate-700" data-testid={`practice-suggestion-${index}`}>{rec}</p>
                </motion.div>
              ))}
            </CardContent>
          </Card>
        )}

        <Card className="border-0 shadow-lg" data-testid="practice-question-review">
          <CardHeader>
            <CardTitle>Question-wise Review</CardTitle>
            <CardDescription>Tag the mistake type for incorrect answers</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {(result.question_summaries || []).map((item, index) => {
              const questionKey = item.local_id || item.question_id || `q-${index}`;
              return (
              <div key={questionKey} className="p-4 rounded-xl border border-slate-200">
                <p className="font-semibold text-slate-900 mb-1">
                  Q{index + 1}. {item.topic && item.question_text?.startsWith(item.topic)
                    ? item.question_text.replace(`${item.topic}: `, '')
                    : item.question_text}
                </p>
                {item.image_url && (
                  <img
                    src={item.image_url}
                    alt="Question diagram"
                    className="w-full max-w-md rounded-xl border border-slate-200 mt-3"
                    data-testid={`practice-result-image-${index}`}
                  />
                )}
                <p className="text-sm text-slate-600">Your answer: {item.student_answer || '—'} · Correct: {item.correct_answer || '—'}</p>
                {item.explanation && (
                  <p className="text-sm text-slate-600 mt-2">Explanation: {item.explanation}</p>
                )}
                {item.solution_image_url && (
                  <img
                    src={item.solution_image_url}
                    alt="Solution diagram"
                    className="w-full max-w-md rounded-xl border border-violet-200 bg-white mt-2"
                    data-testid={`practice-solution-image-${index}`}
                  />
                )}
                {!item.is_correct && (
                  <div className="mt-3">
                    <Select
                      value={item.mistake_type || ''}
                      onValueChange={(value) => handleMistakeChange(questionKey, value)}
                    >
                      <SelectTrigger data-testid={`practice-mistake-${questionKey}`}>
                        <SelectValue placeholder="Select mistake type" />
                      </SelectTrigger>
                      <SelectContent>
                        {mistakeOptions.map((option) => (
                          <SelectItem key={option} value={option}>{option}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            );
            })}

            <Button
              onClick={handleSaveMistakes}
              variant="outline"
              className="rounded-full"
              data-testid="practice-save-mistakes"
            >
              Update Mistake Analysis
            </Button>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle>Book Session Recommendations</CardTitle>
            <CardDescription>Book a teacher for your weak topics</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {result.teacher_recommendations?.length > 0 ? (
              result.teacher_recommendations.map((teacher) => (
                <div key={teacher.id} className="flex items-center justify-between p-4 rounded-xl border border-slate-200">
                  <div>
                    <p className="font-semibold text-slate-900">{teacher.name}</p>
                    <p className="text-sm text-slate-600">{teacher.subjects?.join(', ')}</p>
                  </div>
                  <Link to={`/teacher/${teacher.id}`} className="text-sm text-violet-600 font-medium">
                    Book Session
                  </Link>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-500">No teacher recommendations available yet.</p>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-gradient-to-br from-emerald-50 to-teal-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-emerald-600" />
              Talk to a Counselor
            </CardTitle>
            <CardDescription>Get guidance on the best next steps.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="text-sm text-slate-700">
                <p className="font-semibold">{settings?.counselor_name || 'Education Counselor'}</p>
                <p>{settings?.counselor_whatsapp || 'WhatsApp not set'}</p>
              </div>
              {counselorWhatsapp ? (
                <Button asChild className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full">
                  <a
                    href={`https://wa.me/${counselorWhatsapp}?text=${encodeURIComponent('Hi, I need guidance after my practice test.')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Chat on WhatsApp
                  </a>
                </Button>
              ) : (
                <Button variant="outline" disabled>WhatsApp not available</Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PracticeTestResult;